/** One query hook per domain. Components never fetch directly (CLAUDE.md rule 1). */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InfiniteData } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "@client/lib/api";
import type {
  Appointment, AppointmentKind, AppointmentStatus,
  AssistantStatus, Board, CalendarEvent, Channel, ConflictResult, ConversationDetail, ConversationListItem, DashboardPeriod, DashboardSummary, PeopleList, PeopleView, PlaybookMoment, Product,
  GoogleCalendarSource, GooglePushState,
  GoogleCalendarConnection,
  Message, PatientListItem, Person, PipelineKey, QueueItem, TodaySummary,
} from "@shared/types/index";
import { useUser } from "./useAuth";

export const useToday = () =>
  useQuery<TodaySummary>({ queryKey: ["today"], queryFn: () => apiGet("/api/today") });

export const useDashboard = (period: DashboardPeriod) =>
  useQuery<DashboardSummary>({
    queryKey: ["dashboard", period],
    queryFn: () => apiGet(`/api/dashboard?days=${period}`),
    // Switching the period should swap the numbers, not blank the screen.
    placeholderData: (prev) => prev,
  });

interface ConversationPage { items: ConversationListItem[]; nextCursor: string | null }

/**
 * The inbox list, paged.
 *
 * It used to be one request capped at fifty, so conversation fifty-one was unreachable except by
 * search. The cursor is the last row's `last_message_at` rather than an offset: an offset shifts
 * whenever a new message arrives mid-scroll, which makes rows repeat or disappear.
 */
export const useConversations = (filters: Record<string, string | undefined> = {}) => {
  const query = (cursor?: string) => {
    const entries = Object.entries({ ...filters, cursor })
      .filter((e): e is [string, string] => Boolean(e[1]));
    const qs = new URLSearchParams(entries).toString();
    return apiGet<ConversationPage>(`/api/conversations${qs ? `?${qs}` : ""}`);
  };
  return useInfiniteQuery<ConversationPage>({
    queryKey: ["conversations", filters],
    queryFn: ({ pageParam }) => query(pageParam as string | undefined),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // Typing in the search box should narrow the list, not blank it and bring it back.
    placeholderData: (prev) => prev,
  });
};

/** Counts behind the filter chips. Deliberately independent of the search box. */
export const useConversationCounts = (channelId?: string) =>
  useQuery<{ waiting: number; mine: number; assigned: number; resolved: number; unread: number }>({
    queryKey: ["conversations", "counts", channelId ?? ""],
    queryFn: () => apiGet(`/api/conversations/counts${channelId ? `?channelId=${channelId}` : ""}`),
  });

export const useConversation = (id: string | undefined) =>
  useQuery<ConversationDetail>({
    queryKey: ["conversation", id],
    queryFn: () => apiGet(`/api/conversations/${id}`),
    enabled: Boolean(id),
  });

/**
 * The message appears the moment you send it, marked `sending`, and is replaced by the real row when
 * the server answers. If the send is refused the bubble is removed and the composer keeps the text —
 * losing what someone just typed is the one failure mode worth real care here.
 */
/**
 * A bubble for something that has not reached the server yet.
 *
 * `author` matters more than it looks: the server row carries it and the optimistic one used to set
 * null, so the bubble grew a first-name label on refetch and the thread shifted a line — with no
 * corrective scroll, because the message count had not changed.
 */
function optimistic(
  over: Partial<Message> & Pick<Message, "kind">, author: Message["author"],
): Message {
  return {
    id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    outbound: true, automated: false, body: null,
    mediaPreview: null, mediaType: null, mediaName: null,
    status: "sending", error: null, retryAt: null, author,
    createdAt: new Date().toISOString(),
    voice: false, durationSeconds: null, waveform: null,
    deleted: false, edited: false, transcription: null, quoted: null, reactions: [],
    ...over,
  };
}

/** Appends a message to the cached thread, returning the snapshot to roll back to. */
function appendOptimistic(
  qc: ReturnType<typeof useQueryClient>, conversationId: string, message: Message,
) {
  const previous = qc.getQueryData<ConversationDetail>(["conversation", conversationId]);
  qc.setQueryData<ConversationDetail>(["conversation", conversationId], (old) =>
    old ? { ...old, messages: [...old.messages, message] } : old);
  return previous;
}

export function useSendMessage(conversationId: string) {
  const qc = useQueryClient();
  const { data: me } = useUser();
  return useMutation({
    mutationFn: (body: string) =>
      apiPost(`/api/conversations/${conversationId}/messages`, { body }),
    onMutate: async (body: string) => {
      await qc.cancelQueries({ queryKey: ["conversation", conversationId] });
      const previous = appendOptimistic(qc, conversationId,
        optimistic({ kind: "text", body }, me ? { id: me.id, name: me.name } : null));
      return { previous };
    },
    onError: (_e, _v, context) => {
      if (context?.previous) {
        qc.setQueryData(["conversation", conversationId], context.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["conversation", conversationId] });
      void qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

/**
 * Clears the unread badge for a conversation being read.
 *
 * Deliberately not optimistic in the usual sense — it writes the badge to zero in the list cache
 * immediately because that is what the eye expects, but it never rolls back: failing to clear an
 * unread count is not worth showing anyone an error about.
 */
/**
 * Sends one file.
 *
 * The body is the file itself rather than multipart: a composer attachment is always exactly one
 * file, so the envelope would buy nothing and cost a parser. Metadata rides in the query string.
 */
/** What `kindFromMime` on the server will decide, mirrored so the optimistic bubble matches. */
function kindOf(file: File, voice?: boolean): Message["kind"] {
  if (voice || file.type.startsWith("audio/")) return "audio";
  if (file.type.startsWith("image/")) return file.type === "image/webp" ? "sticker" : "image";
  if (file.type.startsWith("video/")) return "video";
  return "document";
}

export function useSendMedia(conversationId: string) {
  const qc = useQueryClient();
  const { data: me } = useUser();
  return useMutation({
    mutationFn: async (a: { file: File; caption?: string; voice?: boolean; preview?: string }) => {
      const qs = new URLSearchParams({ name: a.file.name });
      if (a.caption) qs.set("caption", a.caption);
      if (a.voice) qs.set("voice", "true");
      const res = await fetch(`/api/conversations/${conversationId}/media?${qs}`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": a.file.type || "application/octet-stream" },
        body: a.file,
      });
      if (!res.ok) {
        const message = await res.json().catch(() => null) as { message?: string } | null;
        throw new Error(message?.message ?? "Não conseguimos enviar o arquivo.");
      }
      return res.json() as Promise<{ id: string }>;
    },

    // Media had no optimistic bubble at all: you pressed send and the thread showed nothing until the
    // upload, the WhatsApp send and a refetch had all finished. The file is already in the browser,
    // so there is no reason to wait for a round trip to show it.
    onMutate: async (a) => {
      await qc.cancelQueries({ queryKey: ["conversation", conversationId] });
      const previous = appendOptimistic(qc, conversationId, optimistic({
        kind: kindOf(a.file, a.voice),
        body: a.caption ?? null,
        // The composer's own preview URL, reused rather than made again — and null for a document,
        // which has nothing to show anyway.
        mediaPreview: a.preview ?? null,
        mediaType: a.file.type || null,
        mediaName: a.file.name,
        voice: Boolean(a.voice),
      }, me ? { id: me.id, name: me.name } : null));
      return { previous };
    },

    onError: (_e, _v, context) => {
      // The composer re-stages whatever did not go, so the bubble must not linger as well — two
      // copies of the same pending file is worse than none.
      if (context?.previous) {
        qc.setQueryData(["conversation", conversationId], context.previous);
      }
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["conversation", conversationId] });
      void qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

/** Transcribes one voice note on demand, for anything the automatic pass missed. */
export function useTranscribe(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      apiPost<{ transcription: string }>(`/api/messages/${messageId}/transcribe`),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["conversation", conversationId] });
    },
  });
}

/**
 * Sets or clears our reaction on a message.
 *
 * Optimistic, because a reaction should feel like a tap and not like a request — but the server is
 * authoritative: it only writes the row once WhatsApp has accepted, so a refused reaction snaps back
 * rather than leaving a chip claiming something the other side never saw.
 */
export function useReact(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string | null }) =>
      apiPost<{ reactions: { emoji: string; mine: boolean }[] }>(
        `/api/messages/${messageId}/react`, { emoji }),

    onMutate: async ({ messageId, emoji }) => {
      await qc.cancelQueries({ queryKey: ["conversation", conversationId] });
      const previous = qc.getQueryData<ConversationDetail>(["conversation", conversationId]);
      qc.setQueryData<ConversationDetail>(["conversation", conversationId], (old) =>
        old
          ? {
              ...old,
              messages: old.messages.map((m) => {
                if (m.id !== messageId) return m;
                // Only ours changes. Theirs stays exactly as it is — the two sides are independent
                // rows, and replacing the whole list here would briefly drop the other person's.
                const theirs = m.reactions.filter((r) => !r.mine);
                return {
                  ...m,
                  reactions: emoji ? [...theirs, { emoji, mine: true }] : theirs,
                };
              }),
            }
          : old);
      return { previous };
    },

    onError: (_e, _v, context) => {
      if (context?.previous) {
        qc.setQueryData(["conversation", conversationId], context.previous);
      }
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["conversation", conversationId] });
    },
  });
}

/**
 * Re-sends one failed message now, instead of waiting out the retry ladder.
 *
 * The ladder's first rung for a network failure is two minutes away, which is no answer at all for
 * someone watching their message fail. The button has existed in `MessageBubble` since it was
 * written and was never reachable — nothing passed `onRetry`.
 */
export function useRetryMessage(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => apiPost(`/api/messages/${messageId}/retry`),
    onMutate: (messageId: string) => {
      // Back to a clock immediately, so pressing it visibly does something. The real status arrives
      // over the event stream a moment later.
      qc.setQueryData<ConversationDetail>(["conversation", conversationId], (old) =>
        old
          ? {
              ...old,
              messages: old.messages.map((m) =>
                m.id === messageId ? { ...m, status: "sending" as const, error: null } : m),
            }
          : old);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["conversation", conversationId] });
    },
  });
}

/**
 * Confirms an appointment, or records that nobody came.
 *
 * Lives in the thread because that is where the nutritionist already is when the patient answers the
 * reminder. The alternative — parsing "sim" out of the reply — turns a stray yes meant for another
 * question into a confirmed consultation.
 */
export function useSetAppointmentStatus(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: "confirmed" | "no_show" | "completed" }) =>
      apiPatch(`/api/appointments/${id}`, { status }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["conversation", conversationId] });
      // Hoje and the Agenda both count appointments by status.
      void qc.invalidateQueries({ queryKey: ["today"] });
      void qc.invalidateQueries({ queryKey: ["appointments"] });
      void qc.invalidateQueries({ queryKey: ["person"] });
    },
  });
}

/** The Google Calendar connection, without any token — those never leave the server. */
export const useGoogleCalendar = () =>
  useQuery<{
    configured: boolean;
    connection: GoogleCalendarConnection | null;
    /** The account's other calendars, read for conflicts. Never includes the ChatNutri one. */
    sources: GoogleCalendarSource[];
    /** Whether appointments are being written back, and why not when they are not. */
    push: GooglePushState | null;
  }>({
    queryKey: ["calendar", "google"],
    queryFn: () => apiGet("/api/calendar/google"),
  });

/** The switch for writing appointments into the ChatNutri calendar. */
export function useToggleAppointmentPush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => apiPatch("/api/calendar/google/push", { enabled }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["calendar", "google"] }),
  });
}

/**
 * Rebuilds the ChatNutri calendar after it was deleted in Google.
 *
 * A button rather than something automatic: a calendar reappearing by itself after you deleted it,
 * every five minutes, is the software arguing with you.
 */
export function useRecreateOurCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost("/api/calendar/google/recreate"),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["calendar", "google"] }),
  });
}

/**
 * Switches one calendar's conflicts on or off.
 *
 * `calendar-events` is invalidated too: switching a calendar off deletes its events server-side, so
 * anything drawing them is now stale.
 */
export function useToggleCalendarSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, selected }: { id: string; selected: boolean }) =>
      apiPatch(`/api/calendar/sources/${id}`, { selected }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["calendar", "google"] });
      void qc.invalidateQueries({ queryKey: ["calendar-events"] });
    },
  });
}

export function useDisconnectGoogleCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete("/api/calendar/google"),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["calendar", "google"] });
      // The events go with the connection, so anything drawing them has to refetch.
      void qc.invalidateQueries({ queryKey: ["calendar-events"] });
    },
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost(`/api/conversations/${id}/read`),
    onMutate: (id: string) => {
      // Two traps here, both of which silently disabled this mutation once the list became paged.
      //
      // `setQueriesData` matches on key *prefix*, so `["conversations"]` also hits
      // `["conversations","counts",…]`, whose data is a plain object. And the list itself is an
      // infinite query now, so its data is `{pages, pageParams}` — not an array. Calling `.map` on
      // either throws, and because query-core awaits `onMutate` inside the same try block that starts
      // the mutation, a throw there means `mutationFn` never runs at all: the request was never sent,
      // the rejection was swallowed by `.mutate()`, and the badge came back on the next refetch.
      qc.setQueriesData<InfiniteData<ConversationPage>>(
        { predicate: (q) => q.queryKey[0] === "conversations" && q.queryKey[1] !== "counts" },
        (old) => (old?.pages
          ? {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                items: page.items.map((c) => (c.id === id ? { ...c, unread: 0 } : c)),
              })),
            }
          : old),
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["conversations"] });
      void qc.invalidateQueries({ queryKey: ["today"] });
    },
  });
}

export function useClaimConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost(`/api/conversations/${id}/claim`),
    // Out of the queue immediately. If somebody else got there first the server answers 409 and the
    // row returns — which is the honest outcome, and rarer than the happy path it speeds up.
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: ["queue"] });
      const previous = qc.getQueryData<QueueItem[]>(["queue"]);
      qc.setQueryData<QueueItem[]>(["queue"], (old) =>
        old?.filter((q) => q.conversationId !== id));
      return { previous };
    },
    onError: (_e, _v, context) => {
      if (context?.previous) qc.setQueryData(["queue"], context.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["queue"] });
      // Covers the counts too: their key starts with "conversations". Claiming empties a row out
      // of "Na fila" and into "Minhas", so both chips are wrong the instant it succeeds.
      void qc.invalidateQueries({ queryKey: ["conversations"] });
      void qc.invalidateQueries({ queryKey: ["today"] });
    },
  });
}

export const useQueue = () =>
  useQuery<QueueItem[]>({ queryKey: ["queue"], queryFn: () => apiGet("/api/queue") });

export const useBoard = (key: PipelineKey) =>
  useQuery<Board>({ queryKey: ["board", key], queryFn: () => apiGet(`/api/boards/${key}`) });

/**
 * Dragging a card moves it immediately and reconciles afterwards.
 *
 * Waiting for the server before the card moves is the single most obvious place the app felt slow: the
 * card springs back under the cursor and then jumps. The shape is the standard one — cancel in-flight
 * refetches, snapshot, apply locally, restore the snapshot if the server refuses.
 */
export function useMoveCard(key: PipelineKey) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stageId, position }: { id: string; stageId: string; position?: number }) =>
      apiPatch(`/api/opportunities/${id}/stage`, position === undefined ? { stageId } : { stageId, position }),
    onMutate: async ({ id, stageId, position = 0 }) => {
      await qc.cancelQueries({ queryKey: ["board", key] });
      const previous = qc.getQueryData<Board>(["board", key]);
      qc.setQueryData<Board>(["board", key], (old) => {
        if (!old) return old;
        const card = old.columns.flatMap((c) => c.cards).find((c) => c.id === id);
        if (!card) return old;
        const fromId = old.columns.find((c) => c.cards.some((k) => k.id === id))?.id;
        const moved = fromId === stageId ? card
          : { ...card, enteredStageAt: new Date().toISOString(), stale: false };
        return {
          ...old,
          columns: old.columns.map((col) => {
            const rest = col.cards.filter((c) => c.id !== id);
            if (col.id !== stageId) return { ...col, cards: rest };
            // Where it was dropped, among the others in their current order — the server renumbers
            // the same way, so the refetch does not reshuffle.
            rest.splice(Math.min(position, rest.length), 0, moved);
            return { ...col, cards: rest };
          }),
        };
      });
      return { previous };
    },
    onError: (_e, _v, context) => {
      if (context?.previous) qc.setQueryData(["board", key], context.previous);
      // Only on failure. The optimistic board is now wrong by definition, so go and find out what
      // is actually true.
      void qc.invalidateQueries({ queryKey: ["board", key] });
    },
    /**
     * On success the board is *not* refetched, and that is deliberate.
     *
     * The optimistic splice above places the card exactly where the server renumbers it to, so a
     * refetch returns the same order it already shows — but it arrives a few hundred milliseconds
     * later, in the middle of the drop animation, and re-renders every card on the board. With a
     * DragOverlay handing the card back to its slot, that lands as a visible twitch right at the
     * moment the drop is meant to feel settled.
     *
     * `today` still refetches, because its counts genuinely are computed server-side and nothing
     * on screen is animating there.
     */
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["today"] });
      void qc.invalidateQueries({ queryKey: ["person"] });
    },
  });
}

export const usePatients = (
  search?: string, opts: { status?: string; followUp?: boolean } = {},
) => {
  const qs = new URLSearchParams();
  if (search) qs.set("search", search);
  if (opts.status) qs.set("status", opts.status);
  if (opts.followUp) qs.set("followUp", "true");
  const query = qs.toString();
  return useQuery<PatientListItem[]>({
    queryKey: ["patients", search ?? "", opts.status ?? "", opts.followUp ? "1" : ""],
    queryFn: () => apiGet(`/api/patients${query ? `?${query}` : ""}`),
    // Keep the current rows on screen while a narrower search resolves.
    placeholderData: (prev) => prev,
  });
};

export interface ContactRow {
  id: string;
  name: string | null;
  phone: string | null;
  /** The channel's own identity for this person — the JID or Instagram scoped id. */
  externalId: string;
  avatarUrl: string | null;
  channel: { id: string; name: string; type: string };
  patient: { id: string; name: string } | null;
  conversationId: string | null;
  lastInteractionAt: string | null;
}

/** `linked: "no"` is the picker behind "Vincular a paciente": contacts no patient has claimed. */
export const useContacts = (
  search?: string, linked?: "yes" | "no", channelId?: string,
) => {
  const qs = new URLSearchParams();
  if (search) qs.set("search", search);
  if (linked) qs.set("linked", linked);
  if (channelId) qs.set("channelId", channelId);
  const query = qs.toString();
  return useQuery<ContactRow[]>({
    queryKey: ["contacts", search ?? "", linked ?? "", channelId ?? ""],
    queryFn: () => apiGet(`/api/contacts${query ? `?${query}` : ""}`),
    // Keep the current rows on screen while a narrower search resolves.
    placeholderData: (prev) => prev,
  });
};

export interface PatientDetail {
  name: string; status: string; phone: string | null; email: string | null; notes: string | null;
  contact: {
    id: string; name: string | null; phone: string | null;
    channel: { name: string; type: string };
  } | null;
  dietPlans: { id: string; title: string; status: string }[];
  // The shared union, not `string`: widening it is what let a raw `no_show` be printed straight
  // into a pt-BR screen without the compiler minding.
  appointments: { id: string; kind: AppointmentKind; status: AppointmentStatus;
                  startsAt: string }[];
  conversations: { id: string; lastMessage: string | null }[];
}

/**
 * One patient's record.
 *
 * Lifted out of `Pacientes.tsx`, which hand-rolled this `useQuery` inline and was the only page in
 * the app fetching outside this file — against the rule at the top of it. The key was already
 * load-bearing: four mutations invalidate `"patient"`, and `usePrefetch` warms it.
 */
export const usePatient = (id: string) =>
  useQuery<PatientDetail>({
    queryKey: ["patient", id],
    queryFn: () => apiGet(`/api/patients/${id}`),
  });

export const useAppointments = (range: { from?: string; to?: string } = {}) => {
  const qs = new URLSearchParams(
    Object.entries(range).filter((e): e is [string, string] => Boolean(e[1])),
  ).toString();
  return useQuery<Appointment[]>({
    queryKey: ["appointments", range],
    queryFn: () => apiGet(`/api/appointments${qs ? `?${qs}` : ""}`),
    // Paging through weeks should slide the calendar, not flash a skeleton.
    placeholderData: (prev) => prev,
  });
};

/** External calendar events for the visible range. Read-only; empty until a calendar is connected. */
export const useCalendarEvents = (range: { from: string; to: string }) =>
  useQuery<CalendarEvent[]>({
    queryKey: ["calendar-events", range],
    queryFn: () => apiGet(`/api/calendar-events?${new URLSearchParams(range).toString()}`),
    placeholderData: (prev) => prev,
  });

/** What overlaps a proposed slot. Only asks once both ends are set and in order. */
export const useAppointmentConflicts = (
  slot: { startsAt?: string | undefined; endsAt?: string | undefined; excludeId?: string | undefined },
) => {
  const ready = Boolean(slot.startsAt && slot.endsAt && slot.startsAt < slot.endsAt);
  const qs = new URLSearchParams(
    Object.entries(slot).filter((e): e is [string, string] => Boolean(e[1])),
  ).toString();
  return useQuery<ConflictResult>({
    queryKey: ["appointments", "conflicts", slot],
    queryFn: () => apiGet(`/api/appointments/conflicts?${qs}`),
    enabled: ready,
  });
};

export const useChannels = () =>
  useQuery<Channel[]>({
    queryKey: ["channels"], queryFn: () => apiGet("/api/channels"),
    // The live stream is what normally refreshes this. The poll is the backstop for a dropped stream,
    // and only while something is wrong: a healthy inbox has no reason to ask every ten seconds.
    refetchInterval: (q) =>
      (q.state.data ?? []).every((c) => c.status === "connected") ? false : 10_000,
    refetchIntervalInBackground: true,
  });

export const useTeam = () =>
  useQuery<{ id: string; name: string; email: string; role: string; active: boolean;
             sectorIds: string[]; pending: boolean }[]>({
    queryKey: ["users"], queryFn: () => apiGet("/api/users"),
  });

/**
 * Which sector answers a channel — the only access control the product has.
 *
 * `channels` because the card re-reads it; `conversations` because what a member may see changes
 * the moment this does (auth/visibility.ts).
 */
export const useUpdateChannel = () =>
  useWrite(({ id, ...i }: { id: string } & Record<string, unknown>) =>
    apiPatch(`/api/channels/${id}`, i), ["channels", "conversations", "queue"]);

/** Configurações › E-mails: the four templates, the queue, and whether sending is configured. */
export const useEmailSettings = () =>
  useQuery<{
    configured: boolean;
    templates: { slug: string; name: string }[];
    outbox: {
      id: string; template: string; templateName: string; to: string; subject: string;
      status: string; attempts: number; lastError: string | null;
      createdAt: string; sentAt: string | null;
    }[];
  }>({ queryKey: ["email"], queryFn: () => apiGet("/api/email") });

export const useSectors = () =>
  useQuery<{ id: string; key: string; name: string }[]>({
    queryKey: ["sectors"], queryFn: () => apiGet("/api/sectors"),
  });

// ─── Mutations ───────────────────────────────────────────────────────────────
//
// One shape for all of them: call the API, then invalidate the query keys the change can be seen
// through. Screens stay free of fetch logic (CLAUDE.md rule 1).


function useWrite<TInput, TResult>(
  fn: (input: TInput) => Promise<TResult>, invalidates: string[],
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const key of invalidates) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

/**
 * Creating a patient is now also a *linking* operation, so it invalidates what linking invalidates.
 *
 * `POST /api/patients` has always accepted `contactId`; nothing ever sent one, so `["patients",
 * "today"]` was a complete list of what could change. "Criar paciente a partir deste contato" sends
 * one, and the first thing you see afterwards is the Contatos row you started from — which kept
 * saying "Vincular" because `["contacts"]` was never invalidated. The record was correct on the
 * server and stale on screen, which is the worst of the two.
 */
export const useCreatePatient = () =>
  useWrite((i: Record<string, unknown>) => apiPost("/api/patients", i),
    ["patients", "today", "contacts", "conversations", "conversation", "person"]);

export interface Measurement {
  id: string; measuredAt: string;
  weightKg: number | null; bodyFatPct: number | null;
  waistCm: number | null; hipCm: number | null; armCm: number | null;
  notes: string | null;
}

/**
 * Newest first, which is also the order the record renders and computes changes in.
 *
 * `enabled` because the patient record calls this at the top of the component while the section
 * that reads it is behind `CLINICAL` (shared/features.ts). Without it the screen would fetch
 * measurements on every patient open to render nothing.
 */
export const useMeasurements = (patientId: string, enabled = true) =>
  useQuery<Measurement[]>({
    queryKey: ["measurements", patientId],
    queryFn: () => apiGet(`/api/patients/${patientId}/measurements`),
    enabled,
  });

export const useCreateMeasurement = (patientId: string) =>
  useWrite((i: Record<string, unknown>) =>
    apiPost(`/api/patients/${patientId}/measurements`, i), ["measurements", "patient", "person"]);

export const useDeleteMeasurement = () =>
  useWrite((id: string) => apiDelete(`/api/measurements/${id}`), ["measurements", "patient", "person"]);

/**
 * Linking a patient to a contact, or clearing the link.
 *
 * The id travels with the call rather than with the hook, because the patient is chosen inside the
 * drawer and is not known at render — the shape `useMoveAppointment` already uses.
 *
 * It invalidates more keys than any other patient mutation, and that is the point of the feature:
 * this one field is read by Contatos, Pacientes, the record, every conversation row and every
 * thread. Until now it could not be written, so none of them ever had to refresh for it.
 */
/**
 * A won deal becomes a patient in Acompanhamento.
 *
 * Invalidates both boards, not just the one you are looking at: the commercial card gains a patient
 * and a new card appears on the care board, and the two screens are the same component with a
 * different key.
 */
export const useConvertOpportunity = () =>
  useWrite(
    (id: string) => apiPost(`/api/opportunities/${id}/convert`, {}),
    ["board", "patients", "patient", "contacts", "today"],
  );

export const useLinkContact = () =>
  useWrite(
    ({ patientId, contactId }: { patientId: string; contactId: string | null }) =>
      apiPatch(`/api/patients/${patientId}`, { contactId }),
    ["contacts", "patients", "patient", "conversations", "conversation"],
  );

export const useUpdatePatient = (id: string) =>
  useWrite((i: Record<string, unknown>) => apiPatch(`/api/patients/${id}`, i),
    ["patients", "patient", "person", "person", "person"]);

// `conversations` is in the invalidation list because the thread shows "Próximo atendimento" and the
// Confirmar / Não veio buttons that hang off it; booking from a conversation has to refresh it.
export const useCreateAppointment = () =>
  useWrite((i: Record<string, unknown>) => apiPost("/api/appointments", i),
    ["appointments", "today", "patient", "dashboard", "conversations", "person"]);

export const useUpdateAppointment = (id: string) =>
  useWrite((i: Record<string, unknown>) => apiPatch(`/api/appointments/${id}`, i),
    ["appointments", "today", "patient", "dashboard", "conversations", "person"]);

/**
 * Moves an appointment, for the drag gesture.
 *
 * Takes the id per call rather than per hook, like `useCloseOpportunity`: the grid does not know which
 * appointment will be dragged until it is.
 */
export const useMoveAppointment = () =>
  useWrite(({ id, ...i }: { id: string } & Record<string, unknown>) =>
    apiPatch(`/api/appointments/${id}`, i),
    ["appointments", "today", "patient", "dashboard", "conversations", "person"]);

/**
 * Removes an appointment outright, as opposed to cancelling it.
 *
 * Cancelling records a real outcome and is what attendance figures are counted from; deleting is for
 * the booking that should never have existed. There was no way to do the second, so a mistyped
 * appointment sat on the grid permanently.
 */
export const useDeleteAppointment = (id: string) =>
  useWrite(() => apiDelete(`/api/appointments/${id}`),
    ["appointments", "today", "patient", "dashboard", "conversations", "person"]);

export const useCreateOpportunity = (key: string) =>
  useWrite((i: Record<string, unknown>) => apiPost("/api/opportunities", i), ["board", key, "today", "person"]);

export const useUpdateOpportunity = () =>
  useWrite(({ id, ...i }: { id: string } & Record<string, unknown>) =>
    apiPatch(`/api/opportunities/${id}`, i), ["board", "today", "person"]);

/** Who someone is, by contact or by patient: the ficha da pessoa (ADR-042). */
/** The people list (design pass, page 6): one list, by view, with every view's count. */
export const usePeople = (view: PeopleView | undefined, search?: string) =>
  useQuery<PeopleList>({
    queryKey: ["patients", "people", view ?? "", search ?? ""],
    queryFn: () => apiGet(`/api/people?${new URLSearchParams({ ...(view ? { view } : {}), ...(search ? { search } : {}) }).toString()}`),
    placeholderData: (prev) => prev,
  });

export type PersonRef = { contactId: string } | { patientId: string };
export const usePerson = (ref: PersonRef | null) =>
  useQuery<Person>({
    queryKey: ["person", ref],
    queryFn: () => apiGet(`/api/person?${new URLSearchParams(ref as Record<string, string>).toString()}`),
    enabled: ref !== null,
  });

/** The ficha's two-line summary, written by the team (F5). Once edited, the assistant leaves it. */
/** "Ler a conversa" (ADR-055): the assistant reads it for Dor, Objetivo and Veio por, now. */
export const useReadConversation = () =>
  useWrite((contactId: string) => apiPost<{ kind: string; found?: { pain: boolean; goal: boolean; source: boolean } }>(
    `/api/person/${contactId}/read`, {}), ["person", "board", "patients"]);

export const useUpdateSummary = () =>
  useWrite(({ contactId, ...i }: { contactId: string; pain?: string | null; goal?: string | null }) =>
    apiPatch(`/api/person/${contactId}/summary`, i), ["person"]);

export const useProducts = () =>
  useQuery<Product[]>({ queryKey: ["products"], queryFn: () => apiGet("/api/products") });

/** The assistant writes a follow-up in the card's thread; the answer says which thread to open. */
export const useFollowUp = () =>
  useWrite((id: string) => apiPost<{ conversationId: string; id: string }>(
    `/api/opportunities/${id}/follow-up`), ["conversation", "today", "person"]);

export const useReorderStages = () =>
  useWrite(({ pipelineId, order }: { pipelineId: string; order: string[] }) =>
    apiPatch(`/api/pipelines/${pipelineId}/order`, { order }), ["board", "pipelines"]);

export const useCloseOpportunity = () =>
  useWrite(({ id, ...i }: { id: string } & Record<string, unknown>) =>
    apiPatch(`/api/opportunities/${id}/close`, i), ["board", "today", "person"]);

export const useStages = () => {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["board"] });
    void qc.invalidateQueries({ queryKey: ["pipelines"] });
    void qc.invalidateQueries({ queryKey: ["playbook"] }); // a stage comes and goes with its moment
  };
  return {
    create: useMutation({
      mutationFn: (i: Record<string, unknown>) =>
        apiPost<{ id: string; momentId: string | null }>("/api/stages", i), onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, ...i }: { id: string } & Record<string, unknown>) =>
        apiPatch(`/api/stages/${id}`, i), onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => apiDelete(`/api/stages/${id}`), onSuccess: invalidate,
    }),
  };
};

export const useQuickReplies = () =>
  useQuery<{ id: string; shortcut: string; body: string }[]>({
    queryKey: ["quick-replies"], queryFn: () => apiGet("/api/quick-replies"),
  });

export const useCreateQuickReply = () =>
  useWrite((i: Record<string, unknown>) => apiPost("/api/quick-replies", i), ["quick-replies"]);

export const useUpdateQuickReply = () =>
  useWrite(({ id, ...i }: { id: string } & Record<string, unknown>) =>
    apiPatch(`/api/quick-replies/${id}`, i), ["quick-replies"]);

export const useDeleteQuickReply = () =>
  useWrite((id: string) => apiDelete(`/api/quick-replies/${id}`), ["quick-replies"]);

export const useScheduleMessage = (conversationId: string) =>
  useWrite((i: { body: string; sendAt: string }) =>
    apiPost(`/api/conversations/${conversationId}/schedule`, i), ["conversation", "scheduled"]);

export const useScheduled = (conversationId: string | undefined) =>
  useQuery<{ id: string; body: string; send_at: string; status: string }[]>({
    queryKey: ["scheduled", conversationId],
    queryFn: () => apiGet(`/api/conversations/${conversationId}/scheduled`),
    enabled: Boolean(conversationId),
  });

/**
 * Pulls the page before the oldest message on screen into the cached thread.
 *
 * Prepended into the same query data rather than kept in a second list, so everything that reads
 * `data.messages` — day dividers, audio auto-advance, reactions — sees one thread.
 */
export const useOlderMessages = (conversationId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (before: string) => apiGet<{ messages: Message[]; hasMore: boolean }>(
      `/api/conversations/${conversationId}/messages?before=${encodeURIComponent(before)}`),
    onSuccess: (page) => {
      qc.setQueryData<ConversationDetail>(["conversation", conversationId], (old) =>
        old ? { ...old, messages: [...page.messages, ...old.messages], hasMore: page.hasMore } : old);
    },
  });
};

export const useCancelScheduled = (conversationId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/api/scheduled/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["scheduled", conversationId] }); },
  });
};

export const useDraftAction = (conversationId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, body }: { id: string; action: "send" | "discard"; body?: string }) =>
      apiPost(`/api/drafts/${id}`, body ? { action, body } : { action }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["conversation", conversationId] });
      void qc.invalidateQueries({ queryKey: ["today"] });
      void qc.invalidateQueries({ queryKey: ["person"] });
    },
  });
};

/**
 * Asks the assistant for a draft right now. The tick does this on its own after each inbound
 * message; the button is for the thread that arrived before the key existed, the one somebody
 * already answered, and the follow-up nobody asked for.
 */
export const useSuggestDraft = (conversationId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (i: { instruction?: string; playbookMessageId?: string } = {}) =>
      apiPost(`/api/conversations/${conversationId}/draft`, i),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["conversation", conversationId] });
      void qc.invalidateQueries({ queryKey: ["today"] });
    },
  });
};

export const usePlaybook = () =>
  useQuery<PlaybookMoment[]>({ queryKey: ["playbook"], queryFn: () => apiGet("/api/playbook") });

export const useUpdatePlaybookMessage = () =>
  useWrite(({ id, ...i }: { id: string } & Record<string, unknown>) =>
    apiPatch(`/api/playbook/messages/${id}`, i), ["playbook"]);

export const useUpdatePlaybookMoment = () =>
  useWrite(({ id, ...i }: { id: string } & Record<string, unknown>) =>
    apiPatch(`/api/playbook/moments/${id}`, i), ["playbook", "board"]);

export const useCreatePlaybookMessage = () =>
  useWrite(({ momentId, ...i }: { momentId: string } & Record<string, unknown>) =>
    apiPost(`/api/playbook/moments/${momentId}/messages`, i), ["playbook", "board"]);

export const useDeletePlaybookMessage = () =>
  useWrite((id: string) => apiDelete(`/api/playbook/messages/${id}`), ["playbook", "board"]);

export const useCreateStageMoment = () =>
  useWrite((stageId: string) => apiPost<{ id: string; key: string }>(`/api/stages/${stageId}/moment`, {}),
    ["playbook", "board"]);

export const useImportPlaybook = () =>
  useWrite((file: unknown) => apiPost<{ updated: number; unknown: string[] }>("/api/playbook/import", file),
    ["playbook"]);

/** The route counts what it did; the screen says so instead of a flat "Textos restaurados." */
export const useResetPlaybookMoment = () =>
  useWrite((id: string) => apiPost<{ restored: number; readded: number }>(
    `/api/playbook/moments/${id}/reset`, {}), ["playbook", "board"]);

/**
 * The practice page (design pass, page 7): what was typed, what the structured settings already
 * answer (working hours, the booking link), and the variables that would still print as a gap.
 */
export interface PracticeView {
  facts: PracticeFacts;
  derived: { horarios: string; link_agenda: string };
  gaps: (keyof PracticeFacts)[];
  /** Facts no enabled script text or quick reply uses: shown quietly, never counted as gaps. */
  unused: (keyof PracticeFacts)[];
}

export interface PracticeFacts {
  clinica: string; nutricionista: string; atendente: string;
  trilha: "ambas" | "online" | "presencial";
  acompanhamento: string; duracao_acompanhamento: string; consulta: string; duracao_consulta: string;
  valor_avista: string; valor_parcelado: string; chave_pix: string;
  link_pagamento: string; link_agenda: string; link_anamnese: string; link_video: string; link_plano: string;
  endereco: string; como_chegar: string; prazo_plano: string; dias_de_retorno: string; horarios: string;
}
export const usePractice = () =>
  useQuery<PracticeView>({ queryKey: ["practice"], queryFn: () => apiGet("/api/settings/practice") });
export const useUpdatePractice = () =>
  useWrite((i: Partial<PracticeFacts>) => apiPatch("/api/settings/practice", i), ["practice", "onboarding"]);

export const useNotALead = (conversationId: string) =>
  useWrite((value: boolean) => apiPost(`/api/conversations/${conversationId}/not-a-lead`, { value }),
    ["conversation", "conversations", "board", "today", "queue", "person"]);

export const useAssistant = () =>
  useQuery<AssistantStatus>({
    queryKey: ["assistant"], queryFn: () => apiGet("/api/settings/assistant"),
  });

export const useUpdateAssistant = () =>
  useWrite((i: Partial<AssistantStatus["settings"]>) => apiPatch("/api/settings/assistant", i),
    ["assistant"]);

export const useUpdateConversation = (id: string) =>
  useWrite((i: Record<string, unknown>) => apiPatch(`/api/conversations/${id}`, i),
    ["conversation", "conversations", "queue", "today", "person"]);

export const useUpdateProfile = () =>
  useWrite((i: Record<string, unknown>) => apiPatch("/api/profile", i), ["auth"]);

/** Creates the person and returns the link they open to set their password (ADR-045). */
export const useInviteUser = () =>
  useWrite((i: Record<string, unknown>) => apiPost<{ id: string; link: string }>("/api/users", i), ["users"]);
export const useInviteLink = () =>
  useWrite((id: string) => apiPost<{ link: string }>(`/api/users/${id}/invite`), []);

export const useUpdateUser = () =>
  useWrite(({ id, ...i }: { id: string } & Record<string, unknown>) =>
    apiPatch(`/api/users/${id}`, i), ["users"]);

export const useUpdateBrand = () =>
  useWrite((i: Record<string, unknown>) => apiPatch("/api/brand", i), ["auth"]);

export const useAvailability = () =>
  useQuery<{ id: string; userId: string; weekday: number; startsAt: string; endsAt: string;
             slotMinutes: number; maxPerDay: number; active: boolean }[]>({
    queryKey: ["availability"], queryFn: () => apiGet("/api/availability"),
  });

export const useSetAvailability = () =>
  useWrite((i: Record<string, unknown>) => apiPost("/api/availability", i), ["availability"]);

export const useUpdateAvailability = () =>
  useWrite(({ id, ...i }: { id: string } & Record<string, unknown>) =>
    apiPatch(`/api/availability/${id}`, i), ["availability"]);

export const useDeleteAvailability = () =>
  useWrite((id: string) => apiDelete(`/api/availability/${id}`), ["availability"]);

/**
 * The follow-up thresholds. Its own endpoint rather than the shared settings blob, so one screen's
 * save cannot erase another's — see the note on `PATCH /api/settings/follow-up`.
 */
export const useFollowUpSettings = () =>
  useQuery<{ reviewDays: number; quietDays: number; leadDays: number }>({
    queryKey: ["settings", "follow-up"], queryFn: () => apiGet("/api/settings/follow-up"),
  });

export const useSaveFollowUpSettings = () =>
  useWrite((i: Record<string, unknown>) => apiPatch("/api/settings/follow-up", i),
    // `patients` too: the thresholds decide who carries a follow-up reason, so the list behind
    // "Precisam de atenção" is stale the moment these change.
    ["settings", "patients"]);

/** Change your own password. The route has existed since 0.12.0 with nothing calling it. */
export const useChangePassword = () =>
  useWrite((i: Record<string, unknown>) => apiPost("/api/auth/password", i), []);

export const useTimeOff = () =>
  useQuery<{ id: string; userId: string | null; startsAt: string; endsAt: string;
             reason: string | null }[]>({
    queryKey: ["time-off"], queryFn: () => apiGet("/api/time-off"),
  });

export const useCreateTimeOff = () =>
  useWrite((i: Record<string, unknown>) => apiPost("/api/time-off", i), ["time-off"]);

export const useDeleteTimeOff = () =>
  useWrite((id: string) => apiDelete(`/api/time-off/${id}`), ["time-off"]);

export const useActivity = (subjectId?: string) =>
  useQuery<{ id: string; action: string; createdAt: string;
             actor: { id: string; name: string } | null }[]>({
    queryKey: ["activity", subjectId ?? ""],
    queryFn: () => apiGet(`/api/activity${subjectId ? `?subjectId=${subjectId}` : ""}`),
  });

export const useCreateChannel = () =>
  useWrite((i: Record<string, unknown>) => apiPost("/api/channels", i), ["channels"]);

/**
 * Asks the server to open a session. The route answers 202 immediately — pairing takes seconds and
 * happens outside the request — so there is deliberately no invalidation here: refetching now would
 * read the row as it was *before* anything happened, which is exactly what left the badge stale. The
 * health transition arrives over the event stream.
 */
export const useConnectChannel = () =>
  useMutation({ mutationFn: (id: string) => apiPost(`/api/channels/${id}/connect`) });

export const useDisconnectChannel = () =>
  useWrite(({ id, forget }: { id: string; forget: boolean }) =>
    apiPost(`/api/channels/${id}/disconnect`, { forget }), ["channels"]);

/**
 * Polls while a channel is mid-connection. The QR arrives asynchronously and rotates every ~20s, so a
 * one-shot fetch would show a code that is already dead.
 */
export const useChannelQr = (id: string | null) =>
  useQuery<{ qr: string | null; status: string }>({
    queryKey: ["channel-qr", id],
    queryFn: () => apiGet(`/api/channels/${id}/qr`),
    enabled: Boolean(id),
    // Only `connected` ends the poll. `disconnected` happens transiently while pairing — the old
    // socket closes before the new code arrives — and stopping there leaves the drawer stuck on
    // "Gerando o código…" forever even though the code did appear.
    refetchInterval: (q) => (q.state.data?.status === "connected" ? false : 3000),
    // Keep polling while the tab is in the background. This is not an edge case: to scan the code the
    // person picks up their phone, which is precisely when the browser stops being focused. Without
    // this the drawer freezes on "Gerando o código…" at the exact moment it is being used.
    refetchIntervalInBackground: true,
  });


/**
 * Warms a thread while the pointer is still on its way to the click.
 *
 * `staleTime` matters more than it looks: without it, moving along a list would refetch every row the
 * cursor crosses. Thirty seconds makes hovering cheap and the eventual click usually instant.
 */
export function usePrefetch() {
  const qc = useQueryClient();
  return {
    conversation: (id: string) => {
      void qc.prefetchQuery({
        queryKey: ["conversation", id],
        queryFn: () => apiGet(`/api/conversations/${id}`),
        staleTime: 30_000,
      });
    },
    patient: (id: string) => {
      void qc.prefetchQuery({
        queryKey: ["patient", id],
        queryFn: () => apiGet(`/api/patients/${id}`),
        staleTime: 30_000,
      });
    },
  };
}

// ─── Agendamento pelo link (ADR-043) ─────────────────────────────────────────

export interface BookingTypeRow {
  id: string; slug: string; name: string; description: string | null; durationMinutes: number;
  modality: "online" | "presencial"; active: boolean; minNoticeHours: number; maxDaysAhead: number;
  bufferMinutes: number;
}
export interface BookingProfile { bio: string | null; notes: string[]; photoUrl: string | null }
export const useBooking = () =>
  useQuery<{ handle: string | null; types: BookingTypeRow[]; profile: BookingProfile }>({
    queryKey: ["booking"], queryFn: () => apiGet("/api/booking"),
  });
/** Bio and "o que esperar" on the public booking page (design pass, page 8). */
export const useSaveBookingProfile = () =>
  useWrite((i: { bio?: string | null; notes?: string[] }) => apiPatch("/api/me/booking-profile", i), ["booking"]);
/** The booking page photo: raw bytes, like a media upload, because it is one file and nothing else. */
export const useUploadPhoto = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const res = await fetch("/api/me/photo", { method: "PUT", body: file, credentials: "include",
                                                  headers: { "content-type": file.type || "application/octet-stream" } });
      const body = await res.json().catch(() => ({})) as { message?: string };
      if (!res.ok) throw new Error(res.status === 413 ? "A foto pode ter até 2 MB." : body.message ?? "Não conseguimos enviar a foto.");
      return body;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["booking"] }),
  });
};
export const useRemovePhoto = () => useWrite(() => apiDelete("/api/me/photo"), ["booking"]);
export const useSetHandle = () =>
  useWrite((handle: string) => apiPatch<{ handle: string }>("/api/me/handle", { handle }), ["booking"]);
export const checkHandle = (handle: string) =>
  apiGet<{ available: boolean; reason: string | null }>(`/api/me/handle/available?handle=${encodeURIComponent(handle)}`);
export const useCreateBookingType = () =>
  useWrite((i: { name: string; durationMinutes: number; modality: "online" | "presencial"; description?: string | null }) =>
    apiPost("/api/booking/types", i), ["booking"]);
export const useUpdateBookingType = () =>
  useWrite(({ id, ...p }: { id: string } & Partial<Omit<BookingTypeRow, "id" | "slug">>) =>
    apiPatch(`/api/booking/types/${id}`, p), ["booking"]);
export const useDeleteBookingType = () =>
  useWrite((id: string) => apiDelete(`/api/booking/types/${id}`), ["booking"]);

// ─── Primeiros passos (P1) ───────────────────────────────────────────────────

export interface ProductRow { id: string; name: string; priceCents: number | null; durationDays: number | null; active: boolean }
export const useAllProducts = () =>
  useQuery<ProductRow[]>({ queryKey: ["products", "all"], queryFn: () => apiGet("/api/products/all") });
export const useCreateProduct = () =>
  useWrite((i: { name: string; priceCents: number | null; durationDays: number | null }) =>
    apiPost("/api/products", i), ["products", "onboarding"]);
export const useUpdateProduct = () =>
  useWrite(({ id, ...p }: { id: string } & Partial<Omit<ProductRow, "id">>) =>
    apiPatch(`/api/products/${id}`, p), ["products", "onboarding"]);

export interface Onboarding {
  items: { key: "whatsapp" | "practice" | "products" | "link"; done: boolean }[];
  done: number; total: number; visible: boolean;
}
export const useOnboarding = () =>
  useQuery<Onboarding>({ queryKey: ["onboarding"], queryFn: () => apiGet("/api/onboarding") });
export const useMarkOnboarding = () =>
  useWrite((step: "practice" | "products" | "link" | "dismiss") =>
    apiPost<Onboarding>(`/api/onboarding/${step}`), ["onboarding"]);
